/**
 * METABOLIC LOGIC: Entry point for energy and insulin simulation logic.
 */

import { BurnCompute } from './metabolic/burn-compute';
import { NutritionCompute } from './metabolic/nutrition-compute';
import { findFIIByHeuristic } from './fii-registry';
import { toLogicDate } from '@/utils/date-utils';

export const MetabolicCompute = {
    ...BurnCompute,
    ...NutritionCompute
};

export const MacronutrientEngine = {
    generateMacroTimeline(events: any[], targetDateStr: string) {
        const [y, m, d] = targetDateStr.split('-').map(Number);
        const targetDate = new Date(y, m - 1, d); // Local Midnight
        const yesterday = new Date(y, m - 1, d - 1);
        const yStr = toLogicDate(yesterday);
        const tStr = targetDateStr;

        const windowEvents = events
            .filter(e => {
                const t = e.type?.toUpperCase();
                const isMetabolic = t === 'NUTRITION' || t === 'NUTRITION_LOG' || t === 'nutrition';
                if (!isMetabolic) return false;
                const dateStr = toLogicDate(e.start_time);
                return dateStr === yStr || dateStr === tStr;
            })
            .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());

        const resultPoints = [];
        const targetMidnight = targetDate.getTime();
        const processedMealIds = new Set();

        // Macro Profiles: { peakTime, clearingTime }
        const PROFILES = {
            carbs: { peak: 45, duration: 180 },
            protein: { peak: 90, duration: 360 },
            fat: { peak: 180, duration: 600 }
        };

        const activeSimulations: { carbs: any[], protein: any[], fat: any[] } = {
            carbs: [], protein: [], fat: []
        };

        let gastricBuffer = 0;

        for (let min = 0; min <= 2880; min++) {
            const currentUnix = targetMidnight - (1440 * 60000) + (min * 60000);
            
            // Stomach clears at roughly 1-2g per minute depending on composition, we'll use a standard 1.0g/min clearing rate
            if (gastricBuffer > 0) gastricBuffer = Math.max(0, gastricBuffer - 1.0);

            for (const meal of windowEvents) {
                const mealStartUnix = new Date(meal.start_time).getTime();
                if (!processedMealIds.has(meal.id) && currentUnix >= mealStartUnix && currentUnix < mealStartUnix + 60000) {
                    const data = { ...(meal.payload || {}), ...(meal.preview_data || {}) };
                    
                    const mealEndUnix = new Date(meal.end_time || meal.start_time).getTime();
                    const eatingDuration = Math.max(15, (mealEndUnix - mealStartUnix) / 60000);

                    // Extract raw amounts
                    const protein = Number(data.protein?.total || data.protein || 0);
                    const carbs = Number(data.carbs?.total || data.carbs || 0);
                    const fat = Number(data.fat?.total || data.fat || 0);
                    const fiber = Number(data.micros?.fiber?.amount || data.micros?.fiber || 0);

                    // Add physical mass to stomach
                    gastricBuffer += (protein + carbs + fat + fiber);

                    if (carbs > 0) activeSimulations.carbs.push({ amount: carbs, peakTime: PROFILES.carbs.peak, duration: eatingDuration, startTimeUnix: mealStartUnix, clearAt: PROFILES.carbs.duration });
                    if (protein > 0) activeSimulations.protein.push({ amount: protein, peakTime: PROFILES.protein.peak, duration: eatingDuration, startTimeUnix: mealStartUnix, clearAt: PROFILES.protein.duration });
                    if (fat > 0) activeSimulations.fat.push({ amount: fat, peakTime: PROFILES.fat.peak, duration: eatingDuration, startTimeUnix: mealStartUnix, clearAt: PROFILES.fat.duration });
                    
                    processedMealIds.add(meal.id);
                }
            }

            const currentLevels = { carbs: 0, protein: 0, fat: 0 };
            
            // Simulate each macro type
            ['carbs', 'protein', 'fat'].forEach((type: string) => {
                const sims = (activeSimulations as any)[type];
                sims.forEach((sim: any) => {
                    const t_since_start = (currentUnix - sim.startTimeUnix) / 60000;
                    // REFINED: Use a large 24h decay window instead of hard technical cutoffs
                    if (t_since_start >= 0 && t_since_start < 1440) {
                        const rate = sim.amount / sim.duration;
                        const eatingMinutesPassed = Math.min(t_since_start, sim.duration);
                        const samples = [0, eatingMinutesPassed / 2, eatingMinutesPassed];
                        let sum = 0;
                        samples.forEach(tau => {
                            const age = t_since_start - tau;
                            if (age >= 0) sum += Math.pow(age / sim.peakTime, 2) * Math.exp((1 - age / sim.peakTime) * 2);
                        });
                        (currentLevels as any)[type] += (rate * sum * eatingMinutesPassed) / samples.length;
                    }
                });
            });

            if (min >= 1440 && min % 5 === 0) {
                resultPoints.push({ 
                    x: ((min - 1440) / 1440) * 100, 
                    carbs: currentLevels.carbs,
                    protein: currentLevels.protein,
                    fat: currentLevels.fat,
                    gastricLoad: gastricBuffer
                });
            }
        }
        return resultPoints;
    }
};

export const InsulinEngine = {
    calculateInsulinLoad(item: any) {
        let fii = findFIIByHeuristic(item.item_name);
        if (fii === null) {
            const proteinImpact = item.protein * 0.45;
            const carbImpact = item.carbs * 1.0;
            const fatImpact = item.fat * 0.1;
            const rawScore = (carbImpact + proteinImpact) / (carbImpact + proteinImpact + fatImpact + 1);
            fii = Math.max(20, Math.min(120, Math.round(rawScore * 100)));
        }
        const load = (fii * item.calories) / 239;
        return Number(load.toFixed(1));
    },

    generateKineticTimeline(events: any[], targetDateStr: string, sensitivity: string = 'NORMAL') {
        const [y, m, d] = targetDateStr.split('-').map(Number);
        const targetDate = new Date(y, m - 1, d); // Local Midnight
        const yesterday = new Date(y, m - 1, d - 1);
        const yStr = toLogicDate(yesterday);
        const tStr = targetDateStr;

        const windowEvents = events
            .filter(e => {
                const t = e.type?.toUpperCase();
                // Include Nutrition, Legacy Logs, and Supplementation in the Insulin Kinetic Timeline
                const isMetabolic = t === 'NUTRITION' || t === 'NUTRITION_LOG' || t === 'SUPPLEMENTATION' || t === 'nutrition';
                if (!isMetabolic) return false;
                const dateStr = toLogicDate(e.start_time);
                return dateStr === yStr || dateStr === tStr;
            })
            .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());

        let state: any = { gastricBuffer: 0, activeSimulations: [] };
        const resultPoints = [];
        const targetMidnight = targetDate.getTime();
        const processedMealIds = new Set();
        const sensPeakMod = sensitivity === 'HIGH' ? 0.8 : (sensitivity === 'LOW' ? 1.3 : 1.0);
        const sensDecayMod = sensitivity === 'HIGH' ? 1.4 : (sensitivity === 'LOW' ? 0.6 : 1.0);

        for (let m = 0; m <= 2880; m++) {
            // currentUnix represents the absolute absolute time for this specific simulation minute
            const currentUnix = targetMidnight - (1440 * 60000) + (m * 60000);
            
            if (state.gastricBuffer > 0) state.gastricBuffer = Math.max(0, state.gastricBuffer - 1.0); // 1.0g/min clearance
            
            for (const meal of windowEvents) {
                // IMPORTANT: Ensure the meal's absolute Unix time is used for injection
                const mealStartUnix = new Date(meal.start_time).getTime();
                
                // If this minute matches the meal's absolute start time
                if (!processedMealIds.has(meal.id) && currentUnix >= mealStartUnix && currentUnix < mealStartUnix + 60000) {
                    const data = { ...(meal.payload || {}), ...(meal.preview_data || {}) };
                    
                    // Handle micros/macros for gastric buffer (Calculated before blunting)
                    const fiber = (typeof data.micros?.fiber === 'object') ? Number(data.micros.fiber.total || 0) : Number(data.micros?.fiber || 0);
                    const protein = (typeof data.protein === 'object') ? Number(data.protein.total || 0) : Number(data.protein || 0);
                    const fat = (typeof data.fat === 'object') ? Number(data.fat.total || 0) : Number(data.fat || 0);
                    
                    // Calculate blunting based on current state BEFORE adding this meal
                    const carryoverBlunting = Math.max(0.65, 1 - (state.gastricBuffer / 120));
                    const strategy = data.sequence_strategy || 'MIXED';
                    
                    // Add this meal's physical mass to stomach
                    state.gastricBuffer += fiber + protein + fat;

                    // Handle potential object structure from Deep Source Mapping
                    const rawLoad = data.insulin_load;
                    const insulinLoadValue = (typeof rawLoad === 'object' && rawLoad !== null && 'total' in rawLoad)
                        ? Number(rawLoad.total)
                        : Number(rawLoad || 0);

                    let peakTime = 45; let strategyMod = 1.0;
                    if (strategy === 'SPIKE') { peakTime = 30; strategyMod = 1.2; }
                    else if (strategy === 'OPTIMIZED') { peakTime = 75; strategyMod = 0.6; }
                    
                    const finalLoad = insulinLoadValue * carryoverBlunting * strategyMod * sensPeakMod;
                    const mealEndUnix = new Date(meal.end_time || meal.start_time).getTime();
                    const duration = Math.max(15, (mealEndUnix - mealStartUnix) / 60000);
                    
                    state.activeSimulations.push({ load: finalLoad, peakTime: peakTime, duration: duration, startTimeUnix: mealStartUnix });
                    
                    processedMealIds.add(meal.id);
                }
            }
            let currentInsulinAtMinute = 0;
            for (const sim of state.activeSimulations) {
                const t_since_start = (currentUnix - sim.startTimeUnix) / 60000;
                // REFINED: Use a large 24h decay window instead of the legacy 8h hard cutoff
                if (t_since_start >= 0 && t_since_start < 1440) {
                    const rate = sim.load / sim.duration;
                    const eatingMinutesPassed = Math.min(t_since_start, sim.duration);
                    const samples = [0, eatingMinutesPassed / 2, eatingMinutesPassed];
                    let sum = 0;
                    samples.forEach(tau => {
                        const age = t_since_start - tau;
                        const adjustedAge = age * sensDecayMod;
                        if (age >= 0) sum += Math.pow(adjustedAge / sim.peakTime, 2) * Math.exp((1 - adjustedAge / sim.peakTime) * 2);
                    });
                    currentInsulinAtMinute += (rate * sum * eatingMinutesPassed) / samples.length;
                }
            }
            if (m >= 1440 && m % 5 === 0) resultPoints.push({ x: ((m - 1440) / 1440) * 100, y: currentInsulinAtMinute });
        }
        return resultPoints;
    }
};
